import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import personContactSchema from '../../schema/personContactSchema';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import { DEFAULT_VALUES } from '../../constants/personContactFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonContact, editPersonContact } from '../../services/userService';

const mapRowToFormValues = (row) =>
  row
    ? {
        ContactMethodType: row.ContactMethod || '',
        ContactType: row.MethodType || '',
        ContactInfo: row.Info || '',
        IsDefaultContact: Boolean(row.IsDefault),
        ContactNotes: row.Notes || '',
      }
    : DEFAULT_VALUES;

const PersonContactDialog = ({ open, onClose, personId, contact = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(contact?.ptsInfoID);

  const methods = useForm({
    resolver: CreateFormResolver(personContactSchema),
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...mapRowToFormValues(contact) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { contactMethodOptions, contactTypeOptions } = useUserDictionaryOptions();
  const contactMethodType = useWatch({ control: methods.control, name: 'ContactMethodType' });

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        PartyType: 'Person',
        ContactMethod: data.ContactMethodType || '',
        MethodType: data.ContactType || '',
        Info: data.ContactInfo || '',
        IsDefault: data.IsDefaultContact || false,
        Notes: data.ContactNotes || '',
        UpdatedBy: currentUser?.Username,
      };

      if (!isEditMode) {
        payload.ptsParentID = personId;
        payload.CreatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode ? editPersonContact(contact.ptsInfoID, payload) : createPersonContact(payload),
        onSuccess: (savedContact) => {
          dispatch(
            notify(
              isEditMode ? 'Contact updated successfully' : 'Contact created successfully',
              'success'
            )
          );
          onSaved?.(savedContact);
        },
      });
    },
    [isEditMode, contact, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this contact method. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Contact Method' : 'Add Contact Method'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!contact?.Created}
            created={contact?.Created}
            createdBy={contact?.CreatedBy}
            updated={contact?.Updated}
            updatedBy={contact?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <RMSSelect
              name="ContactMethodType"
              label="Contact Method"
              options={contactMethodOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="ContactType"
              label="Contact Type"
              options={contactTypeOptions}
              disabled={!contactMethodType}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={8}>
            <RMSTextField
              name="ContactInfo"
              label="Info"
              disabled={!contactMethodType}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSCheckbox name="IsDefaultContact" label="Is Default" />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="ContactNotes"
              label="Notes"
              multiline
              rows={3}
              inputProps={{ maxLength: 5000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonContactDialog;
